import React, { useEffect, useState, useCallback } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const TITLE = { leave: "Leave", overtime: "Overtime", wfh: "Work From Home", official_business: "Official Business", dtr_correction: "DTR Correction" };
const STEP = { Approved: "bg-emerald-50 text-emerald-700 ring-emerald-200", Rejected: "bg-rose-50 text-rose-700 ring-rose-200", Pending: "bg-slate-50 text-slate-600 ring-slate-200" };

export const ChainSteps = ({ chain }) => {
  const steps = (chain || []).filter((s) => s.approver_user_id);
  if (!steps.length) return null;
  const cur = steps.find((s) => s.status === "Pending");
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1" data-testid="approval-chain">
      {steps.map((s, i) => (
        <React.Fragment key={s.step}>
          {i > 0 && <span className="text-slate-300 text-xs">→</span>}
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ${STEP[s.status] || STEP.Pending} ${s === cur ? "ring-amber-300 bg-amber-50 text-amber-700" : ""}`} data-testid={`approval-step-${s.step}`}>{s.step}. {s.name}{s.final ? " (Final)" : ""}{s.status !== "Pending" ? ` · ${s.status}` : s === cur ? " · waiting" : ""}</span>
        </React.Fragment>
      ))}
    </div>
  );
};

const fmtDT = (v) => {
  if (!v) return "—";
  const [d, t] = String(v).includes("T") ? String(v).split("T") : [null, String(v)];
  const [h, m] = t.slice(0, 5).split(":").map(Number);
  const tm = `${String(((h + 11) % 12) + 1).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  return d ? `${fmtDate(d)} ${tm}` : tm;
};

export const RequestDetail = ({ r }) => {
  const lines = [];
  if (r.type === "dtr_correction") {
    if (r.dtr_type !== "time_out") lines.push(["Time In", fmtDT(r.correct_time_in)]);
    if (r.dtr_type !== "time_in") lines.push(["Time Out", fmtDT(r.correct_time_out)]);
  }
  if (r.type === "overtime") lines.push(["Overtime", `${r.start_time ? fmtDT(r.start_time) + " → " + fmtDT(r.end_time) + " · " : ""}${r.hours ?? "—"} hr(s)`]);
  if (r.type === "official_business" && (r.start_time || r.end_time)) lines.push(["Time", `${fmtDT(r.start_time)} → ${fmtDT(r.end_time)}`]);
  if (r.type === "wfh" && r.status === "Approved" && r.salary_option != null) lines.push(["Salary", `${r.salary_option}%`]);
  if (r.leave_type) lines.push(["Leave", `${r.leave_type} · ${r.days ?? "—"} day(s)${r.half_day ? " · Halfday" : ""}${r.with_pay === false ? " · Without Pay" : ""}`]);
  if (r.leave_type && r.start_date) lines.push(["Dates", `${fmtDate(r.start_date)}${r.end_date && r.end_date !== r.start_date ? " → " + fmtDate(r.end_date) : ""}`]);
  if (r.location) lines.push([r.type === "official_business" ? "Destination" : "Location", r.location]);
  if (r.person_to_see) lines.push(["Person to see", r.person_to_see]);
  if (r.attachment_url) lines.push(["Attachment", <a key="a" href={r.attachment_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">View file</a>]);
  if (r.ob_option === "rest_day_offset") lines.push(["Offset", `Rest Day Offset · ${r.rdo_days} day(s), expires ${fmtDate(r.rdo_expires_at)}`]);
  if (r.ob_option === "convert_to_pay") lines.push(["Offset", `Converted to Overtime pay (${(r.converted_dates || []).length} day(s))`]);
  if (r.created_at) lines.push(["Filed", fmtDate(r.created_at)]);
  if (r.status !== "Pending" && r.decided_by) lines.push([r.status === "Approved" ? "Approved by" : "Rejected by", `${r.decided_by}${r.decided_at ? " · " + fmtDate(r.decided_at) : ""}${r.decision_note ? ` · “${r.decision_note}”` : ""}`]);
  const last = !r.decided_by && (r.history || []).slice(-1)[0];
  if (last && r.status !== "Pending Approval") lines.push(["Last action", `${last.action || r.status} · ${last.by || "—"}${last.at ? " · " + fmtDate(last.at) : ""}`]);
  if (!lines.length) return null;
  return (
    <div className="mt-1.5 grid gap-0.5 text-xs" data-testid={`request-detail-${r.id}`}>
      {lines.map(([k, v]) => <div key={k} className="flex gap-2"><span className="text-slate-400 w-20 shrink-0">{k}</span><span className="text-slate-700 tabular-nums">{v}</span></div>)}
    </div>
  );
};

const HIST_STATUS = ["All", "Approved", "Rejected", "Cancelled"];
const History = ({ type, reloadKey }) => {
  const [rows, setRows] = useState([]);
  const [st, setSt] = useState("All");
  useEffect(() => { api.get(type === "leave" ? "/hris/approvals/leave/decided" : `/hris/requests/decided?type=${type}`).then(({ data }) => setRows(data)).catch(() => {}); }, [type, reloadKey]);
  if (!rows.length) return null;
  const shown = st === "All" ? rows : rows.filter((r) => (st === "Rejected" ? ["Rejected", "Declined"].includes(r.status) : r.status === st));
  return (
    <div className="mt-8" data-testid="approval-history">
      <div className="flex flex-wrap items-center gap-2 mb-2"><div className="text-sm font-medium text-slate-800 mr-2">History ({rows.length})</div>
        {HIST_STATUS.map((s) => <button key={s} onClick={() => setSt(s)} className={`text-xs rounded-full px-2.5 py-1 border ${st === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`approval-history-filter-${s}`}>{s}</button>)}
      </div>
      <div className="space-y-2 max-h-[480px] overflow-y-auto">{shown.map((r) => (
        <div key={r.id} className="bg-white rounded-lg border border-slate-200 p-3" data-testid={`approval-history-row-${r.id}`}>
          <div className="flex items-center justify-between gap-2"><span className="font-medium text-sm text-slate-800">{r.employee_name}</span><Badge className={`${r.status === "Approved" ? "bg-emerald-50 text-emerald-700" : r.status === "Cancelled" ? "bg-slate-100 text-slate-500" : "bg-rose-50 text-rose-700"} text-[10px]`}>{r.status}</Badge></div>
          <div className="text-sm text-slate-600">{r.target_date ? `For ${fmtDate(r.target_date)}` : `${r.date_from ? fmtDate(r.date_from) : ""}${r.date_to && r.date_to !== r.date_from ? " → " + fmtDate(r.date_to) : ""}`}{r.reason ? ` · “${r.reason}”` : ""}</div>
          <RequestDetail r={r} />
        </div>
      ))}</div>
    </div>
  );
};

export default function ApprovalQueue({ type }) {
  const isLeave = type === "leave";
  const { user } = useAuth();
  const isSuper = (user?.role || "").toUpperCase() === "SUPER ADMIN";
  const canAct = (it) => {
    const steps = (it.approval_chain || []).filter((s) => s.approver_user_id);
    if (!steps.length || isSuper) return true;
    return steps.find((s) => s.status === "Pending")?.approver_user_id === user?.id;
  };
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState({});
  const [salary, setSalary] = useState({});
  const [histKey, setHistKey] = useState(0);
  const [obOpt, setObOpt] = useState({});

  const load = useCallback(() => {
    const url = isLeave ? "/hris/approvals/leave" : `/hris/requests/pending?type=${type}`;
    api.get(url).then(({ data }) => setItems(data)).catch((e) => { if (e.response?.status === 403) setItems(null); });
  }, [type, isLeave]);
  useEffect(() => { load(); }, [load]);

  const decide = async (item, action) => {
    const url = isLeave ? `/leave/requests/${item.id}/${action}` : `/hris/requests/${item.id}/decision`;
    const body = isLeave ? { reason: notes[item.id] || "" } : { action, note: notes[item.id] || "" };
    if (type === "wfh" && action === "approve") body.salary_option = salary[item.id] ?? 100;
    if (type === "official_business" && action === "approve" && item.rest_or_holiday) {
      if (!obOpt[item.id]) { toast.error("Choose Rest Day Offset or Convert to Pay"); return; }
      body.ob_option = obOpt[item.id];
    }
    try { const { data } = await api.post(url, body); toast.success(data?.next_approver ? `Approved — forwarded to ${data.next_approver}` : action === "approve" ? "Approved" : "Rejected"); load(); setHistKey((k) => k + 1); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  if (items === null) return (
    <div className="uemp-fade-in"><PageHeader title={`For Approval — ${TITLE[type]}`} /><div className="rounded-xl border bg-white p-10 text-center text-slate-500" data-testid="approval-no-access">You are not authorized to approve requests.</div></div>
  );

  return (
    <div className="uemp-fade-in">
      <PageHeader title={`For Approval — ${TITLE[type]}`} subtitle={`${items.length} pending`} />
      <div className="space-y-3" data-testid="approval-queue">
        {items.length === 0 && <div className="rounded-xl border border-dashed bg-white p-10 text-center text-slate-400" data-testid="approval-empty">Nothing pending. You're all caught up.</div>}
        {items.map((it) => (
          <div key={it.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid="approval-row">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-medium text-slate-800">{it.employee_name || it.employee_id}</div>
                <div className="text-xs text-slate-500">{it.department || ""}</div>
                <div className="text-sm text-slate-600 mt-1">
                  {type === "dtr_correction" && it.target_date ? `Correct ${fmtDate(it.target_date)}` : it.target_date ? fmtDate(it.target_date) : null}
                  {it.date_from ? `${fmtDate(it.date_from)}${it.date_to ? " → " + fmtDate(it.date_to) : ""}` : null}
                  {it.start_date ? `${fmtDate(it.start_date)}${it.end_date && it.end_date !== it.start_date ? " → " + fmtDate(it.end_date) : ""} · ${it.days} day(s)${it.half_day ? " (half day)" : ""}` : null}
                  {it.leave_type ? ` · ${it.leave_type}` : ""}
                  {it.location ? ` · ${it.location}` : ""}
                </div>
                {it.reason && <div className="text-xs text-slate-500 mt-1">“{it.reason}”</div>}
                <RequestDetail r={it} />
                <ChainSteps chain={it.approval_chain} />
              </div>
              <Badge className="bg-amber-50 text-amber-700 text-[10px]">Pending</Badge>
            </div>
            {!canAct(it) ? <div className="mt-3 text-xs text-slate-500" data-testid="approval-waiting">Waiting for {(it.approval_chain || []).find((s) => s.status === "Pending")?.name}'s approval.</div> : <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
              {type === "wfh" && (
                <select className="border rounded-lg h-9 px-2 text-sm" value={salary[it.id] ?? 100}
                  onChange={(e) => setSalary({ ...salary, [it.id]: Number(e.target.value) })} data-testid="approval-salary">
                  {[100, 70, 60].map((v) => <option key={v} value={v}>{v}% salary</option>)}
                </select>
              )}
              {type === "official_business" && it.rest_or_holiday && (
                <select className="border rounded-lg h-9 px-2 text-sm" value={obOpt[it.id] || ""} onChange={(e) => setObOpt({ ...obOpt, [it.id]: e.target.value })} data-testid="approval-ob-option">
                  <option value="">Rest day / holiday: choose…</option>
                  <option value="rest_day_offset">Rest Day Offset</option>
                  <option value="convert_to_pay">Convert to Pay</option>
                </select>
              )}
              <Input placeholder="Note (optional)" value={notes[it.id] || ""} onChange={(e) => setNotes({ ...notes, [it.id]: e.target.value })} className="h-9 flex-1" data-testid="approval-note" />
              <Button onClick={() => decide(it, "approve")} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="approval-approve">Approve</Button>
              <Button onClick={() => decide(it, "reject")} variant="outline" className="text-rose-600 border-rose-200 h-9" data-testid="approval-reject">Reject</Button>
            </div>}
          </div>
        ))}
      </div>
      <History type={type} reloadKey={histKey} />
    </div>
  );
}
